/**
 * Qual revenda — isto é, contra qual realm autenticar (H01, ADR-0006).
 *
 * ── O que a H01 pede, e o que a decisão C3 fez com isso ───────────────────
 *
 * A H01 lista *"seleção de tenant quando o usuário pertence a mais de um"*. Isso
 * deixou de existir: a decisão **C3** fixou licença por usuário e uma conta por
 * empresa, imposta pela migração 005 e registrada no cabeçalho de
 * `packages/contracts/src/v1/me.ts`. Uma conta autenticada tem exatamente um
 * vínculo, então `/v1/me` devolve UM tenant e não há o que escolher.
 *
 * Só que a escolha não sumiu — mudou de lugar e de momento. O ADR-0006 dá um
 * realm por tenant (`cliqcrm-<slug>`), e o navegador precisa saber PARA ONDE
 * mandar o usuário logar antes de existir qualquer token. A pergunta virou
 * "de que revenda você é?", feita antes do login, e não "em qual você quer
 * entrar?", feita depois.
 *
 * A troca é a mesma que a C3 previu: quem atende duas revendas tem duas contas
 * e troca fazendo login de novo.
 *
 * ── Por que `sessionStorage` aqui é aceitável ─────────────────────────────
 *
 * Slug não é credencial. É preferência, e a H01 diz exatamente isso:
 * *"seleção de tenant persistida por sessão, mas sempre validada no servidor. O
 * frontend guarda preferência, não autorização."*
 *
 * Quem valida é o `/v1/me`, e antes dele o próprio realm: um slug inventado não
 * tem realm, e o login falha na descoberta. Um slug de OUTRA revenda tem realm,
 * mas as credenciais do usuário não existem lá.
 */

const CHAVE = 'cliqcrm.revenda';

/** Slug de `core.tenants.slug`: minúsculas, dígitos e hífen. */
const FORMATO = /^[a-z0-9](?:[a-z0-9-]{0,60}[a-z0-9])?$/;

export function ehSlugValido(candidato: string): boolean {
  return FORMATO.test(candidato);
}

/**
 * Subdomínios que são AMBIENTE ou infraestrutura, e nunca revenda.
 *
 * ── Por que a lista existe, e o que custou descobrir que faltava um nome ──
 *
 * `stg.cliqcrm.com.br` é o ambiente compartilhado. Sem `stg` aqui, o navegador
 * lia `stg` como slug, procurava o realm `cliqcrm-stg`, e a tela dizia "esta
 * revenda não existe" — mensagem correta para uma pergunta errada, que é o tipo
 * de erro mais caro de diagnosticar: tudo funcionando, e o login sem sair do
 * lugar.
 *
 * A lista é curta de propósito. Cada nome aqui é um slug que uma revenda não
 * pode mais usar, então acrescentar por precaução tira nome de quem tem direito
 * a ele. Entram os que a instalação de fato ocupa.
 */
const NAO_SAO_REVENDA: ReadonlySet<string> = new Set([
  // Infraestrutura.
  'www',
  'app',
  'api',
  'admin',
  // Ambientes da §16.1. `hml` e `homolog` entram pelo mesmo motivo que `stg`:
  // são endereços que já existem no vocabulário de quem opera, e descobrir a
  // ausência de um deles custa a mesma tarde que custou `stg`.
  'stg',
  'staging',
  'hml',
  'homolog',
  'integracao',
]);

/**
 * Segundos níveis que, sob um TLD de país, são SUFIXO e não domínio: em
 * `cliqcrm.com.br`, `com.br` é o que o registro.br vende, e o domínio é
 * `cliqcrm.com.br` inteiro.
 */
const SEGUNDO_NIVEL_DE_PAIS: ReadonlySet<string> = new Set([
  'com',
  'net',
  'org',
  'gov',
  'edu',
  'co',
  'ac',
]);

/**
 * Quantos rótulos o domínio registrado ocupa: `exemplo.com` são dois;
 * `cliqcrm.com.br`, três.
 *
 * Contar "três ou mais rótulos" como sinal de subdomínio acertava em `.com` e
 * errava em `.com.br`: a produção fica na raiz de `cliqcrm.com.br`, e o
 * navegador lia `cliqcrm` como revenda (#592).
 */
function rotulosDoDominio(partes: readonly string[]): number {
  const tld = partes.at(-1) ?? '';
  const segundo = partes.at(-2) ?? '';
  return tld.length === 2 && SEGUNDO_NIVEL_DE_PAIS.has(segundo) ? 3 : 2;
}

/**
 * O subdomínio, quando ele for a revenda.
 *
 * `alfa.cliqcrm.com.br` → `alfa`. Em `localhost` e em IP não há subdomínio, e a
 * função devolve `null` em vez de inventar — é o que faz a tela de entrada
 * aparecer no ambiente local e não aparecer em produção.
 *
 * Nome de ambiente ou de infraestrutura também devolve `null`, e o efeito é o
 * mesmo e desejado: em `stg.cliqcrm.com.br` a tela pergunta de que revenda a
 * pessoa é, em vez de adivinhar `stg`.
 */
export function revendaDoSubdominio(hospedeiro: string): string | null {
  // Endereço IP não tem subdomínio, e `127.0.0.1` engana a contagem de rótulos:
  // são quatro partes, e `127` passa no formato do slug porque dígito é
  // permitido. Quem acessa por IP acabaria tentando logar num realm
  // `cliqcrm-127`. Vale para IPv6 pelo mesmo motivo.
  if (/^[\d.]+$/.test(hospedeiro) || hospedeiro.includes(':')) return null;

  const partes = hospedeiro.split('.');
  if (partes.length <= rotulosDoDominio(partes)) return null;

  const primeira = partes[0];
  if (!primeira || NAO_SAO_REVENDA.has(primeira)) return null;
  return ehSlugValido(primeira) ? primeira : null;
}

export function lembrarRevenda(slug: string): void {
  sessionStorage.setItem(CHAVE, slug);
}

export function esquecerRevenda(): void {
  sessionStorage.removeItem(CHAVE);
}

/**
 * A revenda desta sessão, na ordem em que as fontes valem.
 *
 * O subdomínio ganha da preferência guardada de propósito: em produção o
 * endereço é a fonte da verdade, e uma preferência velha no `sessionStorage`
 * não pode fazer `alfa.cliqcrm.com.br` tentar logar no realm da `beta`.
 */
export function revendaAtual(hospedeiro = location.hostname): string | null {
  const doEndereco = revendaDoSubdominio(hospedeiro);
  if (doEndereco) return doEndereco;

  const guardada = sessionStorage.getItem(CHAVE);
  return guardada && ehSlugValido(guardada) ? guardada : null;
}

/** O realm do Keycloak desta revenda (ADR-0006). */
/**
 * O realm de identidade — um só, para todas as revendas.
 *
 * ── O que mudou, e o que a revenda passou a escolher ────────────────
 *
 * Era `cliqcrm-${slug}`: um realm por revenda, e a pessoa escolhia a revenda
 * ANTES de digitar a senha, porque o navegador precisava saber para onde
 * mandar o login.
 *
 * Agora a senha é conferida num lugar só, e a revenda escolhe o **client**
 * dentro dele (`clientDaRevenda`, em `fluxo-oidc.ts`). O `tenant_id` continua
 * sendo carimbado pelo provedor — só que por um mapper do client em vez de um
 * do realm.
 *
 * O nome está escrito aqui e em `packages/modulos/acesso/src/adaptadores/keycloak/realm.ts`,
 * e são as duas pontas da mesma convenção: a interface não importa `@cliqcrm/modulo-acesso`
 * (§17.1). Um caso em cada lado afirma o mesmo literal.
 */
export const REALM_DE_IDENTIDADE = 'cliqcrm';
